/**
 * useUnsavedChangesGuard.ts — the shared "unsaved changes" navigation guard.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: patient-journey
 * navigation resilience (feat/patient-journey-navigation-resilience).
 *
 * WHY: a patient can lose typed-but-unsaved input three ways — browser Back/
 * Forward, closing/refreshing the tab, or an in-app "Back" button that only
 * changes local component state. One hook covers all three:
 *
 *   1. `useBlocker` — blocks router navigations (Link / navigate / browser
 *      Back) while dirty. Browser Back only works when the history entry was
 *      created by `@remix-run/router` (it skips blockers when `delta == null`
 *      — silent in production). The journey stamps `?assessment=` with
 *      `replace: true` and never pushes stage URLs, so Back would otherwise
 *      leave the SPA with no dialog. When `dirty` rises we optionally PUSH a
 *      same-URL entry via `navigate` (router-owned, so Back has a delta).
 *      Surfaces that already have a real PUSH underneath (CallbackPage —
 *      RouteChoice pushes `/callback`) MUST pass `{ armBrowserBack: false }`
 *      so an extra arming entry does not break in-app `navigate(-1)`.
 *   2. `beforeunload` — native "leave site?" for tab close/refresh.
 *   3. `guardedAction` — in-app Back / stage changes that are not router nav.
 *
 * Arming is deferred one macrotask so a rapid dirty→clean (type + submit in
 * the same test/user burst) never leaves an orphan same-URL history slot —
 * that pollution broke callback Back-drain tests.
 *
 * The arming PUSH itself is allowed via `skipBlockRef`, NOT by exempting any
 * nextLocation that still carries `__unsavedGuard`. Exempting guard-marked
 * destinations let browser Back from a dirty `/callback` onto a leftover
 * armed journey entry skip the leave dialog entirely.
 *
 * Router hooks (`useBlocker` / `useNavigate` / `useLocation`) are wrapped so
 * unit mounts of QuestionFlow outside a `<Router>` still get beforeunload +
 * guardedAction (journey.spec capture auto-submit).
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  useBlocker,
  useLocation,
  useNavigate,
  type Blocker,
  type BlockerFunction,
  type Location,
  type NavigateFunction,
} from 'react-router-dom';
type SafeBlocker = Blocker | null;
function useSafeBlocker(shouldBlock: BlockerFunction | boolean): SafeBlocker {
  try {
    // Deterministic per mount. The try/catch guards the router-LESS render tree
    // (unit tests, the embedded widgets), it does not make the call conditional:
    // the hook runs on every render or the component never mounts at all.
    return useBlocker(shouldBlock);
  } catch {
    return null;
  }
}
const NOOP_NAVIGATE: NavigateFunction = () => {};
const FALLBACK_LOCATION = { pathname: '/', search: '', hash: '', state: null, key: 'default' } as Location;
function useSafeNavigate(): NavigateFunction {
  try {
    // Deterministic per mount — see useSafeBlocker above.
    return useNavigate();
  } catch {
    return NOOP_NAVIGATE;
  }
}
function useSafeLocation(): Location {
  try {
    // Deterministic per mount — see useSafeBlocker above.
    return useLocation();
  } catch {
    return FALLBACK_LOCATION;
  }
}
const GUARD_STATE_KEY = '__unsavedGuard';
function hasGuardState(state: unknown): boolean {
  return (
    typeof state === 'object' &&
    state !== null &&
    Boolean((state as { [GUARD_STATE_KEY]?: boolean })[GUARD_STATE_KEY])
  );
}
export interface UnsavedChangesGuardOptions {
  /**
   * When true (default), rising `dirty` pushes a same-URL history entry via
   * the data router so browser Back has a remix-router `delta` and useBlocker
   * can fire. Set false when the surface already sits on a real PUSH (e.g.
   * CallbackPage) — an extra arming entry would make in-app `navigate(-1)`
   * land on the same page instead of the previous route.
   */
  armBrowserBack?: boolean;
}
export interface UnsavedChangesGuard {
  blocker: SafeBlocker;
  promptOpen: boolean;
  guardedAction: (action: () => void) => void;
  confirmLeave: () => void;
  cancelLeave: () => void;
}
export function useUnsavedChangesGuard(
  dirty: boolean,
  options: UnsavedChangesGuardOptions = {},
): UnsavedChangesGuard {
  const armBrowserBack = options.armBrowserBack !== false;
  const navigate = useSafeNavigate();
  const location = useSafeLocation();
  const armedRef = useRef(false);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  // Skip blocking (a) the dirty-arming PUSH and (b) the in-app leave
  // navigation that confirmLeave runs — otherwise Leave re-triggers
  // useBlocker and the dialog never closes.
  const skipBlockRef = useRef(false);
  const blockerFn = useCallback<BlockerFunction>(() => {
    if (skipBlockRef.current) return false;
    return dirty;
  }, [dirty]);
  const blocker = useSafeBlocker(blockerFn);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  useEffect(() => {
    if (!dirty) return;
    function handleBeforeUnload(event: BeforeUnloadEvent) {
      event.preventDefault();
      event.returnValue = '';
    }
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [dirty]);
  useLayoutEffect(() => {
    if (dirty && armBrowserBack) {
      if (armedRef.current) return;
      if (hasGuardState(location.state)) {
        armedRef.current = true;
        return;
      }
      // Defer the PUSH so a sync dirty→clean burst (Capture fill+submit) never
      // commits an orphan arming entry before collapse can run.
      const path = location.pathname;
      const search = location.search;
      const hash = location.hash;
      const prevState = location.state;
      const timer = window.setTimeout(() => {
        if (!dirtyRef.current || armedRef.current) return;
        armedRef.current = true;
        skipBlockRef.current = true;
        navigate(
          { pathname: path, search, hash },
          {
            state: {
              ...(typeof prevState === 'object' && prevState ? prevState : {}),
              [GUARD_STATE_KEY]: true,
            },
            preventScrollReset: true,
          },
        );
        window.setTimeout(() => {
          skipBlockRef.current = false;
        }, 0);
      }, 0);
      return () => window.clearTimeout(timer);
    }
    // Dirty cleared — drop the arming entry if we are still sitting on it so
    // Continue/submit does not leave an extra same-URL history slot (breaks
    // callback Back-drain tests and real browser Back stacks).
    if (!armedRef.current) return;
    armedRef.current = false;
    if (hasGuardState(location.state)) {
      skipBlockRef.current = true;
      navigate(-1);
      window.setTimeout(() => {
        skipBlockRef.current = false;
      }, 0);
    }
  }, [dirty, armBrowserBack, navigate, location.pathname, location.search, location.hash, location.state]);
  useEffect(() => {
    if (!dirty && blocker?.state === 'blocked') blocker.reset?.();
  }, [dirty, blocker]);
  const guardedAction = useCallback(
    (action: () => void) => {
      if (!dirty) {
        action();
        return;
      }
      setPendingAction(() => action);
    },
    [dirty],
  );
  const confirmLeave = useCallback(() => {
    if (blocker?.state === 'blocked') blocker.proceed?.();
    if (pendingAction) {
      const run = pendingAction;
      setPendingAction(null);
      skipBlockRef.current = true;
      try {
        run();
      } finally {
        window.setTimeout(() => {
          skipBlockRef.current = false;
        }, 0);
      }
    }
  }, [blocker, pendingAction]);
  const cancelLeave = useCallback(() => {
    if (blocker?.state === 'blocked') blocker.reset?.();
    setPendingAction(null);
  }, [blocker]);
  const promptOpen = blocker?.state === 'blocked' || pendingAction !== null;
  return { blocker, promptOpen, guardedAction, confirmLeave, cancelLeave };
}
